import { hasValue } from '@repo/utils';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { v4 } from 'uuid';

import { OperationAbortedError } from 'src/errors';
import type { TTableSettingsModel } from 'src/services/user-settings-service/types';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';
import { GOPLAN_TABLE_SYSTEM_TEMPLATES_EDITING } from 'src/store/roles-store/roles';
import type { RolesStore } from 'src/store/roles-store/roles-store';

import type { TWellTableTemplate, TWellTableTemplates, WellsTableTemplatesApi } from './wells-table-templates.api';

export class WellsTableTemplateStore {
  private readonly api: WellsTableTemplatesApi;
  private readonly notifications: NotificationsStore;
  private readonly rolesStore: RolesStore;
  @observable isTemplatesUpdating = false;
  @observable isTemplatesInitiated = false;
  @observable editTemplateState: 'new' | TWellTableTemplate | null = null;
  @observable selectedTemplate: TWellTableTemplate | null = null;
  @observable defaultColumnsOrderModel: string[] = [];

  @observable private abortController: AbortController | null = null;

  @observable templates: TWellTableTemplates = {
    private: [],
    system: [],
  };
  templatesMap = new Map<string, TWellTableTemplate>();

  constructor(api: WellsTableTemplatesApi, notifications: NotificationsStore, rolesStore: RolesStore) {
    this.api = api;
    this.notifications = notifications;
    this.rolesStore = rolesStore;

    makeObservable(this);
  }

  @action.bound
  setEditTemplateModalState(state: 'new' | TWellTableTemplate | null): void {
    this.editTemplateState = state;
  }

  @action.bound
  setSelectedTemplate(template: TWellTableTemplate): void {
    this.selectedTemplate = template;
  }

  @action.bound
  setDefaultColumnsOrderModel(columnsOrderModel: string[]): void {
    this.defaultColumnsOrderModel = columnsOrderModel;
  }

  abortTemplatesUpdating = () => {
    this.abortController?.abort();
  };

  @computed
  get isSystemCreationAvailable(): boolean {
    return this.rolesStore.hasAccess([GOPLAN_TABLE_SYSTEM_TEMPLATES_EDITING]) && this.templates.system.length < 5;
  }

  @computed
  get isPrivateCreationAvailable(): boolean {
    return this.templates.private.length < 5;
  }

  @flow.bound
  async *copyTemplate(template: TWellTableTemplate, type: 'private' | 'system'): Promise<void> {
    const privateTemplates = [...this.templates.private];
    const systemTemplates = [...this.templates.system];

    const updatedPrivateTemplates = [...privateTemplates];
    const updatedSystemTemplates = [...systemTemplates];

    if (type === 'private') {
      updatedPrivateTemplates.push({ ...template, id: v4(), type: 'private' });
    } else {
      updatedSystemTemplates.push({ ...template, id: v4(), type: 'system' });
    }

    this.templates = {
      private: updatedPrivateTemplates,
      system: updatedSystemTemplates,
    };

    this.abortController?.abort();
    this.abortController = new AbortController();

    this.isTemplatesUpdating = true;

    try {
      this.templates = await this.api.updateTemplates(
        {
          private: updatedPrivateTemplates,
          system: updatedSystemTemplates,
        },
        this.abortController.signal,
      );
      yield;

      this.templatesMap.clear();
      this.templates.private.forEach((template) => {
        this.templatesMap.set(template.id, template);
      });
      this.templates.system.forEach((template) => {
        this.templatesMap.set(template.id, template);
      });
    } catch (e) {
      yield;
      if (e instanceof OperationAbortedError) {
        return;
      }

      console.error(e);
      this.notifications.showErrorMessageT('wells:Errors.failedToCopyTemplate');
      this.templates = {
        private: privateTemplates,
        system: systemTemplates,
      };
    } finally {
      this.isTemplatesUpdating = false;
      this.abortController = null;
    }
  }

  @flow.bound
  async *deleteTemplate(id: string): Promise<void> {
    const privateTemplates = [...this.templates.private];
    const systemTemplates = [...this.templates.system];

    const filteredPrivateTemplates = privateTemplates.filter((template) => template.id !== id);
    const filteredSystemTemplates = systemTemplates.filter((template) => template.id !== id);

    this.templates = {
      private: filteredPrivateTemplates,
      system: filteredSystemTemplates,
    };

    this.isTemplatesUpdating = true;
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      this.templates = await this.api.updateTemplates(
        {
          private: filteredPrivateTemplates,
          system: filteredSystemTemplates,
        },
        this.abortController.signal,
      );
      yield;

      this.templatesMap.clear();
      this.templates.private.forEach((template) => {
        this.templatesMap.set(template.id, template);
      });
      this.templates.system.forEach((template) => {
        this.templatesMap.set(template.id, template);
      });
    } catch (e) {
      yield;
      if (e instanceof OperationAbortedError) {
        return;
      }

      console.error(e);
      this.notifications.showErrorMessageT('wells:Errors.failedToDeleteTemplate');

      this.templates.private = privateTemplates;
      this.templates.system = systemTemplates;
    } finally {
      this.isTemplatesUpdating = false;
      this.abortController = null;
    }
  }

  @flow.bound
  async *createOrUpdateTemplate(
    name: string,
    description: string,
    type: 'private' | 'system',
    template: TWellTableTemplate | null,
    tableSettings: TTableSettingsModel | null,
    onSuccess: () => void,
  ) {
    const updatedTemplates: {
      private: TWellTableTemplate[];
      system: TWellTableTemplate[];
    } = {
      private: [...this.templates.private],
      system: [...this.templates.system],
    };

    if (hasValue(template)) {
      if (type === 'private') {
        updatedTemplates.private = updatedTemplates.private.map((t) => {
          if (t.id === template.id) {
            const updatedTemplate: TWellTableTemplate = { ...t, name, description };

            updatedTemplate.data = {
              filterModel: tableSettings?.filterModel ?? t.data.filterModel ?? { items: [] },
              columnsOrderModel:
                tableSettings?.columnsOrderModel ?? t.data.columnsOrderModel ?? this.defaultColumnsOrderModel,
              columnsVisibility: t.data.columnsVisibility ?? tableSettings?.columnsVisibility ?? {},
              columnsWidth: tableSettings?.columnsWidth ?? t.data.columnsWidth ?? {},
              sortingModel: tableSettings?.sortingModel ?? t.data.sortingModel ?? [],
              pinnedColumns: tableSettings?.pinnedColumns ?? t.data.pinnedColumns ?? {},
            };

            return updatedTemplate;
          }

          if (!t.id) {
            return {
              ...t,
              id: v4(),
              filterModel: tableSettings?.filterModel ?? t.data.filterModel ?? { items: [] },
              columnsOrderModel:
                tableSettings?.columnsOrderModel ?? t.data.columnsOrderModel ?? this.defaultColumnsOrderModel,
              columnsVisibility: tableSettings?.columnsVisibility ?? t.data.columnsVisibility ?? {},
              columnsWidth: tableSettings?.columnsWidth ?? t.data.columnsWidth ?? {},
              sortingModel: tableSettings?.sortingModel ?? t.data.sortingModel ?? [],
              pinnedColumns: tableSettings?.pinnedColumns ?? t.data.pinnedColumns ?? {},
            };
          }

          return t;
        });
      } else {
        updatedTemplates.system = updatedTemplates.system.map((t) => {
          if (t.id === template.id) {
            const updatedTemplate: TWellTableTemplate = { ...t, name, description };

            if (tableSettings) {
              updatedTemplate.data = {
                filterModel: tableSettings.filterModel ?? t.data.filterModel ?? { items: [] },
                columnsOrderModel:
                  tableSettings.columnsOrderModel ?? t.data.columnsOrderModel ?? this.defaultColumnsOrderModel,
                columnsVisibility: tableSettings.columnsVisibility ?? t.data.columnsVisibility ?? {},
                columnsWidth: tableSettings.columnsWidth ?? t.data.columnsWidth ?? {},
                sortingModel: tableSettings.sortingModel ?? t.data.sortingModel ?? [],
                pinnedColumns: tableSettings.pinnedColumns ?? t.data.pinnedColumns ?? {},
              };
            }

            return updatedTemplate;
          }

          if (!t.id) {
            return { ...t, id: v4() };
          }

          return t;
        });
      }
    } else {
      if (type === 'private') {
        const newTemplate: TWellTableTemplate = {
          id: v4(),
          name,
          description,
          type: 'private',
          data: {},
        };

        if (tableSettings) {
          newTemplate.data = tableSettings;
        }

        updatedTemplates.private.push(newTemplate);
      } else {
        const newTemplate: TWellTableTemplate = {
          id: v4(),
          name,
          description,
          type: 'system',
          data: {},
        };

        if (tableSettings) {
          newTemplate.data = tableSettings;
        }

        updatedTemplates.system.push(newTemplate);
      }
    }

    this.isTemplatesUpdating = true;
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const savedTemplates = await this.api.updateTemplates(updatedTemplates, this.abortController.signal);
      yield;

      this.templates = savedTemplates;
      this.templatesMap.clear();
      this.templates.private.forEach((template) => {
        this.templatesMap.set(template.id, template);
      });
      this.templates.system.forEach((template) => {
        this.templatesMap.set(template.id, template);
      });

      this.setEditTemplateModalState(null);
      onSuccess();
    } catch (e) {
      if (e instanceof OperationAbortedError) {
        return;
      }

      console.error(e);
      this.notifications.showErrorMessageT('wells:Errors.failedToCreateOrUpdateTemplate');
    } finally {
      this.isTemplatesUpdating = false;
      this.abortController = null;
    }
  }

  @flow.bound
  async *loadTemplates() {
    try {
      const templates = await this.api.getTemplates();
      yield;

      this.templates = templates;
      this.templatesMap.clear();

      templates.private.forEach((template) => {
        this.templatesMap.set(template.id, template);
      });
      templates.system.forEach((template) => {
        this.templatesMap.set(template.id, template);
      });

      this.isTemplatesInitiated = true;
    } catch (error) {
      yield;
      console.error(error);
      this.notifications.showErrorMessageT('wells:Errors.failedToLoadTemplates');
    }
  }
}
